import { focusManager, useQueryClient } from '@tanstack/react-query'
import { Hub } from 'aws-amplify/utils'
import { noop } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSetAgentId } from '~/features/agents/hooks/useSetAgentId'
import useCompleteSignIn from '~/features/auth/hooks/useCompleteSignIn'
import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import { queries } from '~/features/auth/utils'
import useRedirectListener from '~/hooks/useRedirectListener'
import { EAdminPortalClientPath, EClientPath } from '~/models/api'
import { ECognitoGroups, TCustomOAuthState } from '~/models/auth'
import { sessionStore, store } from '~/store'

const useAuthListener = () => {
  useRedirectListener()
  const queryClient = useQueryClient()
  const setAgentId = useSetAgentId()
  const [setUser, resetStore, signInRedirectPath] = store((state) => [
    state.setUser,
    state.resetStore,
    state.user.signInRedirectPath,
  ])
  const [isLoggedInPreviously, setIsLoggedInPreviously] = useState(false)

  const resetSessionStore = sessionStore((state) => state.resetStore)

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const { completeSignIn } = useCompleteSignIn()
  const { refetch } = useGetAuthSessionQuery()

  const handleNavigation = useCallback(async () => {
    const { data: authSession } = await refetch()

    if (signInRedirectPath) {
      navigate(signInRedirectPath)
      setUser({ signInRedirectPath: null })

      return
    }

    if (authSession?.groups.includes(ECognitoGroups.partnerAdmin)) {
      navigate(EClientPath.partnerAdmin)
    } else if (authSession?.groups.includes(ECognitoGroups.partnerEditor)) {
      navigate(EClientPath.partnerAdminUserManagement)
    } else if (authSession?.groups?.includes(ECognitoGroups.admin)) {
      navigate(EAdminPortalClientPath.admin)
    } else {
      navigate(EClientPath.overview, {
        state: { previousPathname: pathname },
      })
    }
  }, [navigate, pathname, refetch, setUser, signInRedirectPath])

  const onSignOut = useCallback(() => {
    resetStore(['user.signInRedirectPath'])
    resetSessionStore()
    queryClient
      .getQueryCache()
      .findAll()
      .forEach((query) => query.setData(undefined))
    queryClient.invalidateQueries()
    queryClient.resetQueries()
    setAgentId(undefined)
  }, [queryClient, resetStore, resetSessionStore, setAgentId])

  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'customOAuthState':
          if (payload.data) {
            const customState = JSON.parse(payload.data) as TCustomOAuthState
            if (customState.signInRedirectPath) {
              setUser({ signInRedirectPath: customState.signInRedirectPath })
            }
          }
          break
        case 'signedIn':
          handleNavigation().then(() => completeSignIn())
          break
        case 'signedOut':
        case 'tokenRefresh_failure':
          onSignOut()

          break
        default:
          break
      }
    })

    return unsubscribe
  }, [completeSignIn, handleNavigation, onSignOut, setUser])

  useEffect(() => {
    focusManager.setEventListener((handleFocus) => {
      if (typeof window !== 'undefined' && window.addEventListener) {
        const visibilitychangeHandler = () => {
          if (document.visibilityState !== 'visible') {
            handleFocus(false)
            return
          }

          queryClient
            .fetchQuery({
              ...queries.authSession(),
              staleTime: 0,
            })
            .then((authSession) => {
              const isLoggedInNow = !!authSession?.cognitoUid

              if (!isLoggedInNow && isLoggedInPreviously) {
                setIsLoggedInPreviously(isLoggedInNow)
                onSignOut()
                handleFocus(document.visibilityState === 'visible')
              } else if (isLoggedInNow && !isLoggedInPreviously) {
                completeSignIn().then(() => {
                  setIsLoggedInPreviously(isLoggedInNow)
                  queryClient.invalidateQueries()
                  handleFocus(document.visibilityState === 'visible')
                })
              } else {
                setIsLoggedInPreviously(isLoggedInNow)
                handleFocus(document.visibilityState === 'visible')
              }
            })
            .catch(() => {
              onSignOut()
              handleFocus(document.visibilityState === 'visible')
            })
        }

        window.addEventListener(
          'visibilitychange',
          visibilitychangeHandler,
          false,
        )
        window.addEventListener('focus', visibilitychangeHandler, false)

        return () => {
          window.removeEventListener(
            'visibilitychange',
            visibilitychangeHandler,
          )
          window.removeEventListener('focus', visibilitychangeHandler)
        }
      }

      return noop
    })
  }, [completeSignIn, isLoggedInPreviously, onSignOut, queryClient])
}

export default useAuthListener
